import React, { useMemo, useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { IoMdGlobe } from 'react-icons/io';
import { StarRating } from '../profile/customersurveyform';
import CandidateListModal from '../teams/candidatelistmodal';
import { useProfileStore } from '../../state/profileStore';
import { useMatrixStore } from '../../state/matrixStore';

export default function LocalUsersTeamList({
  users = [],
  loading = false,
  onHoverUser,
  onSelectUser,

  // NEW: Globe toggle (will be wired from TeamInfo in the next step)
  isGlobalSearch = false,
  onToggleGlobalSearch = () => {},

}) {

  // ▼ Get the current signed-in user ID here
  const { profile } = useProfileStore();
  const viewerUId = Number(profile?.u_id) || null;

  // Matrix presence map for avatar borders
  const presenceMap = useMatrixStore(s => s.presence);





  console.log('Users data received:', users);
  console.log('First user focus data:', users[0]?.u_customer_focus);
  // Anchor to Column 3: measure this list’s wrapper rect
  const containerRef = useRef(null);
  const [topOffset, setTopOffset] = useState(0);
  const [rightOffset, setRightOffset] = useState(0);

  const computeOffsets = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    // Align TOP with the top of Column 3; align RIGHT with Column 3's right edge
    setTopOffset(Math.round(rect.top + window.scrollY));
    setRightOffset(Math.round(window.innerWidth - rect.right));
  };

  useLayoutEffect(() => {
    computeOffsets();
    window.addEventListener('resize', computeOffsets);
    return () => window.removeEventListener('resize', computeOffsets);
  }, []);

  // Local modal state (opens from this list)
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  // NEW — search UI
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Paging for the right-column list
  const [visibleCount, setVisibleCount] = useState(15);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);

  const openModal = (user) => {
    console.log('[LocalUsersTeamList] openModal user:', user);
    console.log('[LocalUsersTeamList] openModal user.b_name:', user?.b_name);
    setSelectedCandidate(user || null);
    setShowCandidateModal(true);
  };
  const closeModal = () => setShowCandidateModal(false);
  // Debug: confirm what the modal receives when it opens
  useEffect(() => {
    if (showCandidateModal) {
      console.log('[LocalUsersTeamList] modal opening with candidate:', selectedCandidate);
      console.log('[LocalUsersTeamList] modal candidate.b_name:', selectedCandidate?.b_name);
    }
  }, [showCandidateModal, selectedCandidate]);

// Also open the same modal when the map (green pin) is clicked
  useEffect(() => {
    const handler = (e) => { if (e?.detail) openModal(e.detail); };
    window.addEventListener('candidate:openModal', handler);
    return () => window.removeEventListener('candidate:openModal', handler);
  }, []);

  if (users.length > 0) {
    console.log('All field names in first user:', Object.keys(users[0]));
    console.log('First user complete data:', users[0]);
  }

  // When a new result set arrives, start with 15 again
  useEffect(() => {
    setVisibleCount(Math.min(15, users.length || 0));
    setLoadingMore(false);
  }, [users]);

  const DRAG_MIME = 'application/ehandshake-candidate';
  const onDragStartUser = (e, row) => {
    try {
      const candidate_u_id = Number(row?.id ?? row?.raw?.u_id ?? row?.raw?.id);
      if (!Number.isFinite(candidate_u_id)) return;
      e.dataTransfer.setData(DRAG_MIME, JSON.stringify({ candidate_u_id }));
      // Optional: nicer preview if you have an avatar element:
      // const img = new Image(); img.src = row.avatar; e.dataTransfer.setDragImage(img, 16, 16);
    } catch {}
  };

  const rows = useMemo(() => {
      if (!Array.isArray(users)) return [];
      
      const processedRows = users.map((u) => {

        const first = String(u?.u_first_name ?? '').trim();
        const last  = String(u?.u_last_name ?? '').trim();
        const name  = [first, last].filter(Boolean).join(' ') || 'User';

        // Business + Industry (allow either key naming)
        const business = String(u?.b_name ?? u?.b_business_name ?? '').trim();
        const industry = String(u?.b_business_industry ?? u?.b_industry ?? '').trim();

        // Ratings (handle multiple shapes)
        const avg   = u?.u_avg_rating ?? u?.avg_rating ?? u?.rating ?? null;
        const count = u?.u_review_count ?? u?.review_count ?? u?.reviews ?? 0;

        const avatar = u?.u_profile_picture
          ? `${process.env.REACT_APP_API_URL || ''}${u.u_profile_picture}`
          : '/default-avatar.png';

        const slug = u?.slug || u?.u_id || u?.id;
        const focus = String(u?.u_customer_focus ?? u?.b_customer_focus ?? u?.customer_focus ?? u?.focus_val ?? '').trim();

        return {
          id: u?.u_id || u?.id,
          key: u?.u_id || u?.id || `${name}-${business}`,
          name,
          business,
          industry,
          avg: avg != null ? Number(avg) : null,
          count: Number(count || 0),
          avatar,
          slug,
          raw: u,
          focus,
        };
      });

      console.log('Processed rows focus data:', processedRows.map(r => ({name: r.name, focus: r.focus})));
      return processedRows;
    }, [users]);

  // NEW: filter rows in-memory based on searchTerm
  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return rows;

    return rows.filter((r) => {
      const parts = [
        r.name,
        r.business,
        r.industry,
        r.focus,
        r.raw?.u_email,
        r.raw?.email,
        r.raw?.username,
        r.raw?.u_username,
      ];

      const haystack = parts
        .filter(Boolean)
        .map((p) => String(p).toLowerCase())
        .join(' ');

      return haystack.includes(term);
    });
  }, [rows, searchTerm]);

  useEffect(() => {
    const contains109 = rows.some((r) => Number(r.id) === 109);
    console.log('[LocalUsersTeamList][SEARCH DEBUG]', {
      viewerUId,
      searchTerm,
      rowsLen: rows.length,
      filteredLen: filteredRows.length,
      contains109,
    });

    if (contains109) {
      const brettRow = rows.find((r) => Number(r.id) === 109);
      console.log('[LocalUsersTeamList][SEARCH DEBUG] row 109 snapshot:', {
        id: brettRow?.id,
        name: brettRow?.name,
        business: brettRow?.business,
        industry: brettRow?.industry,
        focus: brettRow?.focus,
        email: brettRow?.raw?.u_email || brettRow?.raw?.email,
      });
    }
  }, [viewerUId, searchTerm, rows, filteredRows]);

  // NEW: when search term changes, reset visibleCount for the filtered list
  useEffect(() => {
    const base = Math.min(15, filteredRows.length || 0);
    setVisibleCount(base);
    setLoadingMore(false);
  }, [searchTerm, filteredRows.length]);

  // Fetch Matrix presence for visible users via server-side admin API
  useEffect(() => {
    const visible = filteredRows.slice(0, visibleCount);
    const uids = visible.map(r => Number(r.id)).filter(n => Number.isFinite(n) && n > 0);
    if (!uids.length) return;

    let cancelled = false;

    const fetchPresence = async () => {
      try {
        const res = await fetch(`/api/matrix/presence?uids=${uids.join(',')}`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || !data?.ok) return;
        const store = useMatrixStore.getState();
        for (const [uid, presence] of Object.entries(data.presence)) {
          store.setUserPresence(`@u${uid}:ehandshake.com`, presence);
        }
      } catch (err) {
        console.warn('[LocalUsersTeamList] presence fetch error:', err);
      }
    };

    fetchPresence();
    const interval = setInterval(fetchPresence, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [filteredRows, visibleCount]);

  // IntersectionObserver: when the 10th item of the CURRENT page is visible, load 15 more
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (loadingMore) return;
        if (visibleCount >= filteredRows.length) return;

        setLoadingMore(true);
        // small throbber delay
        setTimeout(() => {
          setVisibleCount((n) => Math.min(n + 15, filteredRows.length));
          setLoadingMore(false);
        }, 300);
      },
      {
        root: containerRef.current,       // scroll container
        threshold: 0.5,
      }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [visibleCount, filteredRows.length, loadingMore]);


  const hasUsers = filteredRows.length > 0;

  return (
    <div
      ref={containerRef}
      className="w-full min-w-0 box-border bg-white rounded-lg p-0 shadow-[0_0_20px_rgba(0,0,150,0.1)] h-[550px] overflow-y-scroll pr-2 relative scrollbar-thin scrollbar-thumb-gray-100 scrollbar-track-transparent"
    >
      {/* Sticky header + search */}
      <div className="sticky top-0 z-10 bg-white px-4 pt-4 pb-3 border-b">
        <div className="flex items-center justify-center relative">
          <h2 className="text-xl font-bold text-[#00aef7]">Find Teammates</h2>

          {/* Search Icon */}
          <button
            onClick={() => setSearchOpen(true)}
            className="absolute right-0 p-2 hover:bg-[#00aef7]/10 rounded-full transition"
            title="Search teammates"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="22"
              height="22"
            >
              <circle
                cx="11"
                cy="11"
                r="6"
                fill="none"
                stroke="#00aef7"
                strokeWidth="2"
              />
              <line
                x1="15"
                y1="15"
                x2="20"
                y2="20"
                stroke="#00aef7"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {searchOpen && (
          <div className="mt-2">
            <div className="flex items-center gap-[6px]">
              <div
                className="
                  flex-1 flex items-center
                  border border-[#00aef7]
                  rounded-lg
                  bg-white
                  px-2 py-1.5
                  shadow-sm
                "
              >
                <input
                  autoFocus
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search for teammates..."
                  className="
                    flex-1
                    bg-transparent
                    outline-none
                    text-sm
                    text-gray-800
                    placeholder-gray-400
                  "
                />

                {searchTerm.length > 0 && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setSearchOpen(false);
                    }}
                    className="
                      ml-2 text-[#00aef7]
                      text-base font-bold
                      hover:text-[#007bb5]
                      transition
                    "
                    title="Clear search"
                    type="button"
                  >
                    ✕
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={() => {
                  // Local toggle so the button stays visually “ON” immediately.
                  // Next step: TeamInfo will pass real isGlobalSearch + handler to control the actual dataset.
                  onToggleGlobalSearch();
                }}
                title={isGlobalSearch ? 'Global search is ON' : 'Global search'}
                className={[
                  "shrink-0 w-[28px] h-[28px]",
                  "flex items-center justify-center",
                  "rounded-md transition",
                  "outline-none focus:outline-none focus:ring-0 ring-0",
                  isGlobalSearch
                    ? "bg-[#00aef7] text-white shadow-inner"
                    : "bg-white text-gray-400 hover:text-[#00aef7]"
                ].join(" ")}
              >
                <IoMdGlobe className="text-[18px]" />
              </button>
            </div>
          </div>
        )}
      </div>



      <div className="px-4 py-4">
        {loading ? (

          // Skeleton while loading
          <ul className="space-y-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <li key={i}>
                <div className="p-3 bg-gray-50 rounded-xl border border-[#00aef7]/10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-200" />
                    <div className="flex-1">
                      <div className="w-40 h-4 bg-gray-200 rounded" />
                      <div className="w-48 h-3 bg-gray-200 rounded mt-2" />
                      <div className="w-36 h-3 bg-gray-200 rounded mt-2" />
                      <div className="w-28 h-3 bg-gray-200 rounded mt-3" />
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : !hasUsers ? (
          <div className="bg-gray-50 border border-[#00aef7]/10 rounded-xl shadow-[0_4px_14px_rgba(0,174,247,0.08)] p-4 text-sm text-gray-700">
            No local users match your focus yet.
          </div>
        ) : (
          <>
            <ul className="space-y-3">
              {filteredRows.slice(0, visibleCount).map((r, idx) => (
                <li key={r.key}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => openModal(r.raw)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        openModal(r.raw);
                      }
                    }}
                    onMouseEnter={() => onHoverUser && onHoverUser(r.id)}
                    onMouseLeave={() => onHoverUser && onHoverUser(null)}
                    className="w-full text-left"
                  >
                    <div
                      className="
                        p-3 bg-gray-50 rounded-xl
                        border border-[#00aef7]/10
                        shadow-[0_4px_14px_rgba(0,174,247,0.08)]
                        hover:shadow-[0_6px_20px_rgba(0,174,247,0.12)]
                        transition-shadow
                        cursor-grab active:cursor-grabbing
                      "
                      draggable
                      onDragStart={(e) => {
                        e.stopPropagation();
                        onDragStartUser(e, r);
                      }}
                      title="Drag to the Master Recruiting List"
                    >
                      <div className="flex items-start gap-3">
                        {/* Left column: avatar + focus badge */}
                        <div className="flex flex-col items-center">
                          <img
                            src={r.avatar}
                            alt={r.name}
                            className={`w-10 h-10 rounded-full object-cover flex-shrink-0 ${
                              presenceMap[`@u${r.id}:ehandshake.com`] === 'online'
                                ? 'border-2 border-green-500'
                                : presenceMap[`@u${r.id}:ehandshake.com`] === 'unavailable'
                                  ? 'border-2 border-yellow-500'
                                  : 'border border-[#00aef7]/30'
                            }`}
                          />
                          {r.focus && (
                            <span
                              className="mt-1 inline-flex items-center px-1.5 py-[2px] text-[10px] font-bold rounded-full
                                        bg-blue-50 text-[#00aef7] border border-[#00aef7]/20"
                              title="Customer focus"
                            >
                              {r.focus.toUpperCase()}
                            </span>
                          )}
                        </div>
                        {/* Right column: text content */}
                        <div className="flex-1 min-w-0">
                          {/* Row 1: User name */}
                          <div className="text-sm font-semibold text-gray-900 break-words">
                            {r.name}
                          </div>

                          {/* Row 2: Business name */}
                          <div className="text-xs text-gray-800 mt-0.5 break-words">
                            {r.business || '—'}
                          </div>

                          {/* Row 3: Industry */}
                          <div className="text-xs text-gray-500 mt-0.5 break-words">
                            {r.industry || '—'}
                          </div>

                          {/* Bottom: star rating */}
                          <div className="mt-2">
                            {r.count > 0 && r.avg != null ? (
                              <div className="flex items-center gap-2">
                                <StarRating rating={r.avg} />
                                <span className="text-xs text-gray-600">
                                  {r.avg.toFixed(1)} ({r.count})
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-500">No ratings yet</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {(() => {
                    const sentinelIndex =
                      Math.min(visibleCount, filteredRows.length) - 6; // 10th item of current page
                    return idx === sentinelIndex &&
                      visibleCount < filteredRows.length ? (
                      <div ref={sentinelRef} className="h-1" />
                    ) : null;
                  })()}
                </li>
              ))}
            </ul>

            {loadingMore && (
              <div className="py-3 text-center text-xs text-gray-500">Loading more…</div>
            )}
          </>
        )}
      </div>

      {/* Modal (opens on top; title = clicked user’s name) */}
{showCandidateModal && createPortal(
  <CandidateListModal
    opened
    onClose={closeModal}
    top={topOffset}
    right={rightOffset}
    offsetRight={15}
    candidate={selectedCandidate}
    candidateName={[
      selectedCandidate?.u_first_name,
      selectedCandidate?.u_last_name
    ].filter(Boolean).join(' ') || 'Users Information'}
    viewerUId={viewerUId}   // ← EXACTLY THIS LINE
  />,
  document.body
)}




    </div>
  );
}

